import { useState, useRef } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { useQuery } from 'convex/react'
import { api } from '../../convex/_generated/api'
import { sanitizeInput, validateFormData, setCSRFToken } from '../utils/security'
import { contactService } from '../utils/api'
import './Contact.css'

function Contact() {
  const recaptchaRef = useRef(null)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  })

  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)
  const [recaptchaToken, setRecaptchaToken] = useState(null)

  // Set up CSRF token
  useState(() => {
    setCSRFToken()
  }, [])

  const validationRules = {
    name: {
      required: true,
      label: 'Name',
      minLength: 2,
      maxLength: 50
    },
    email: {
      required: true,
      type: 'email'
    },
    phone: {
      required: false,
      type: 'phone'
    },
    subject: {
      required: true,
      label: 'Subject',
      minLength: 3,
      maxLength: 100
    },
    message: {
      required: true,
      label: 'Message',
      minLength: 10,
      maxLength: 1000
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: sanitizeInput(value)
    }))
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate form
    const newErrors = validateFormData(formData, validationRules)
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    // Validate reCAPTCHA
    if (!recaptchaToken) {
      setSubmitStatus({
        type: 'error',
        message: 'Please complete the reCAPTCHA verification.'
      })
      return
    }

    setLoading(true)
    setSubmitStatus(null)

    try {
      await contactService.submit({ ...formData, recaptchaToken })
      setSubmitStatus({
        type: 'success',
        message: 'Thank you! Your message has been sent. We will contact you soon.'
      })
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      })
      setRecaptchaToken(null)
      recaptchaRef.current?.reset()
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Failed to send message. Please try again or contact us directly.'
      })
    } finally {
      setLoading(false)
    }
  }

  const settings = useQuery(api.siteSettings.get)
  const email = settings?.contactEmail ?? 'admin@omnitax.ca'
  const phone = settings?.contactPhone ?? '403-369-3711'
  const cell = settings?.contactCell ?? '403-700-1368'
  const address = settings?.address ?? 'B7, 260300 Writing Creek Cres\nRockyview County, AB, T4A-0XB'

  return (
    <div className="contact">
      <div className="container">
        <section className="contact-intro">
          <h1>Contact Us</h1>
          <p>Get in touch with our team for any questions or to schedule a consultation.</p>
        </section>

        <div className="contact-content">
          {/* Contact Information */}
          <section className="contact-info">
            <h2>Contact Information</h2>

            <div className="info-item">
              <h3>Email</h3>
              <a href={`mailto:${email}`}>{email}</a>
            </div>

            <div className="info-item">
              <h3>Phone</h3>
              <p><a href={`tel:${phone}`}>Work: {phone}</a></p>
              <p><a href={`tel:${cell}`}>Cell: {cell}</a></p>
            </div>

            <div className="info-item">
              <h3>Address</h3>
              <p>
                {address.split('\n').map((line, i) => (
                  <span key={i}>{line}{i < address.split('\n').length - 1 && <br/>}</span>
                ))}
                <br/>Canada
              </p>
            </div>
          </section>

          {/* Contact Form */}
          <section className="contact-form-section">
            <h2>Send us a Message</h2>
            
            {submitStatus && (
              <div className={`alert alert-${submitStatus.type}`}>
                {submitStatus.message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={errors.name ? 'error' : ''}
                  disabled={loading}
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={errors.email ? 'error' : ''}
                  disabled={loading}
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={errors.phone ? 'error' : ''}
                  placeholder="403-000-0000"
                  disabled={loading}
                />
                {errors.phone && <span className="error-message">{errors.phone}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="subject">Subject *</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className={errors.subject ? 'error' : ''}
                  disabled={loading}
                />
                {errors.subject && <span className="error-message">{errors.subject}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  rows="6"
                  value={formData.message}
                  onChange={handleChange}
                  className={errors.message ? 'error' : ''}
                  disabled={loading}
                ></textarea>
                {errors.message && <span className="error-message">{errors.message}</span>}
              </div>

              <div className="form-group">
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
                  onChange={(token) => setRecaptchaToken(token)}
                  onExpired={() => setRecaptchaToken(null)}
                />
              </div>

              <button type="submit" className="btn btn-primary btn-large" disabled={loading}>
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </section>
        </div>
      </div>
    </div>
  )
}

export default Contact
